import { useNavigate } from "react-router";

import CoachingList from "@/../../hypetrain/static/img/exec/coaching-block.png";
import {
  Meeting,
  UserMeeting,
} from "@/components/pieces/coaching/meetings/MeetingList";
import { Coach } from "@/components/pieces/coaching/YourCoaches";
import { getUser } from "@/components/session/JwtTokenManager";
import { Button } from "@/components/ui/button";
import { AssignmentType, MeetingType } from "@/graphql";

import { HomeBlock } from "./HomeBlock";

const sortMeetings = (meetings: UserMeeting[], desc: boolean) => {
  return meetings.sort(function (a, b) {
    if (!a.eventAt || !b.eventAt) return 0;
    var keyA = new Date(a.eventAt),
      keyB = new Date(b.eventAt);
    if (keyA < keyB) return desc ? 1 : -1;
    if (keyA > keyB) return desc ? -1 : 1;
    return 0;
  });
};

interface CoachingBlockProps {
  upcomingMeetings?: MeetingType[];
  assignments?: AssignmentType[];
  availableCredits?: number;
}

export function CoachingBlock({
  assignments,
  upcomingMeetings,
  availableCredits,
}: CoachingBlockProps) {
  const navigate = useNavigate();
  const user = getUser();

  const isEmpty =
    !upcomingMeetings?.length && !assignments?.length && !availableCredits;

  const renderContent = () => {
    if (isEmpty)
      return (
        <div className="h-full overflow-hidden grayscale group-hover:grayscale-0 md:min-h-50">
          <div className="relative h-full">
            <img
              className="h-full origin-top-right object-cover transition-all duration-300 ease-out group-hover:scale-105"
              src={CoachingList}
            />
          </div>
        </div>
      );

    if (upcomingMeetings?.length) {
      const sortedMeetings = sortMeetings(upcomingMeetings, false);

      return (
        <div className="xs:p-5 xs:gap-3.5 flex flex-col md:gap-4 md:p-6">
          <div className="xs:text-lg font-semibold md:text-xl">
            Upcoming Sessions
          </div>

          {sortedMeetings.map((meeting) => (
            <Meeting key={meeting.id} meeting={meeting} mt={0} />
          ))}
        </div>
      );
    }

    if (assignments?.length)
      return (
        <div className="xs:p-5 xs:gap-3.5 flex flex-col md:gap-4 md:p-6">
          <div className="xs:text-lg font-semibold md:text-xl">
            Your Coaches
          </div>
          <div className="xs:gap-3.5 flex flex-wrap md:gap-4">
            {assignments.map((assignment) => (
              <div
                key={assignment.id}
                className="w-full min-w-50 sm:max-w-full md:max-w-[calc(50%-16px)] lg:max-w-[calc(33.333%-16px)]"
              >
                <Coach
                  userId={user!.id}
                  assignment={assignment}
                  viewAsCoach={false}
                  onRemove={() => navigate(0)}
                  workspaceId={null}
                />
              </div>
            ))}
          </div>
        </div>
      );

    if (availableCredits)
      return (
        <div className="xs:p-5 md:p-6">
          <div className="xs:p-3.5 xs:rounded-md xs:gap-3 flex flex-col bg-gradient-to-br from-[#F2FAFF] to-[#D8ECF8] md:gap-4 md:rounded-lg md:p-6">
            <div className="xs:gap-3.5 flex flex-col md:gap-4">
              <div className="text-lg font-semibold text-blue-800">
                Meet with coaches and find your match
              </div>
              <div className="text-base font-normal text-gray-700">
                You have credits available to meet with a coach.
              </div>
            </div>

            <div>
              <Button
                className="xs:px-7 xs:py-2.5 md:px-12 md:py-3"
                onClick={() => navigate("/match")}
                variant="solid"
                intent="primary"
              >
                <span className="xs:text-sm font-semibold md:text-base">
                  Find My Coach
                </span>
              </Button>
            </div>
          </div>
        </div>
      );
  };

  return (
    <HomeBlock
      key="coaching-home-block"
      title="1:1 Coaching"
      description="Fast-track your growth, schedule time with top ranked career and life coaches."
      content={renderContent()}
      isEmpty={isEmpty}
      onClick={() => {
        navigate("/coaching");
      }}
    />
  );
}
